import { Box, SimpleGrid, Stack, Text, Title, Tooltip, useMantineColorScheme } from "@mantine/core";
import { isString } from "lodash";

import { LabelValue } from "../LabelValue";

import { IconInfo } from "@/components/icons";

import type { MantineStyleProps, SimpleGridProps } from "@mantine/core";
import type { FC, ReactNode } from "react";
import type { LabelValueProps } from "../LabelValue";

export type ItemSet = {
  cols?: SimpleGridProps["cols"];
  title?: ReactNode;
  titleTooltip?: string;
  items: LabelValueProps[];
};

export type DataGridProps = (
  | {
      itemSets: ItemSet[];
      items?: never;
      title?: never;
    }
  | ({
      itemSets?: never;
    } & ItemSet)
) & {
  cols?: SimpleGridProps["cols"];
  gridProps?: SimpleGridProps;
  horizontal?: boolean;
} & MantineStyleProps;

export const DataGrid: FC<DataGridProps> = ({
  cols = { base: 2, sm: 4 },
  title,
  gridProps,
  items,
  itemSets,
  horizontal,
  ...props
}) => {
  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const textColor = isDark ? "gray.4" : "dark";

  const effectiveItemSets: ItemSet[] = itemSets
    ? [...itemSets]
    : items
      ? [{ title: title, items: items }]
      : [];

  return (
    <Stack {...props}>
      {effectiveItemSets.map((itemSet, setIndex) => (
        <Stack key={setIndex}>
          {itemSet.title && isString(itemSet.title) ? (
            <Title
              order={4}
              c={textColor}
              w="100%"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4em",
              }}
            >
              {itemSet.title}
              {itemSet.titleTooltip && (
                <Tooltip
                  events={{ hover: true, focus: true, touch: true }}
                  label={
                    <>
                      <Text
                        style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.2" }}
                        fz="sm"
                      >
                        {itemSet.titleTooltip}
                      </Text>
                    </>
                  }
                  position="top"
                  offset={4}
                  p="md"
                  maw={380}
                  openDelay={100}
                  withinPortal
                  style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  <Box
                    component="span"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <IconInfo fill="var(--mantine-color-secondary-4)" />
                  </Box>
                </Tooltip>
              )}
            </Title>
          ) : (
            itemSet.title
          )}

          <SimpleGrid cols={itemSet.cols || cols} {...gridProps}>
            {itemSet.items.map((item, index) => (
              <LabelValue
                key={index}
                horizontal={horizontal}
                c={textColor}
                titleTooltip={item.titleTooltip}
                {...item}
              />
            ))}
          </SimpleGrid>
        </Stack>
      ))}
    </Stack>
  );
};
